import type { DisciplineBalancesIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getDisciplineBalancesFilterParams } from '../lib/discipline-balances-query'
import { useDisciplineBalancesStore } from '../model/balances/store'

interface DisciplineBalancesApiOptions {
  disciplineId: string | number
}

/** Filters and pages change faster than the list answers, so only the latest request may land. */
let activeRequest: AbortController | null = null

export const abortDisciplineBalancesApi = (): void => {
  activeRequest?.abort()
  activeRequest = null
  useDisciplineBalancesStore().setLoading(false)
}

/** `GET /admin-api/disciplines/{discipline}/balances` — the lesson-balance history, newest first. */
export const useDisciplineBalancesApi = async (
  options: DisciplineBalancesApiOptions,
): Promise<DisciplineBalancesIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useDisciplineBalancesStore()

  const url = useAdminRoutes('admin.disciplines.api.balances', pick({
    discipline_id: options.disciplineId,
    ...getDisciplineBalancesFilterParams(store.getFilters),
    per_page: store.getPerPage,
    page: store.getCurrentPage,
  }))

  activeRequest?.abort()
  const request = new AbortController()
  activeRequest = request

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: request.signal })

    if (request.signal.aborted) return null

    const indexData: DisciplineBalancesIndexData | null = data.data || null

    if (indexData) {
      store.setBalances(indexData.balances)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch (err: unknown) {
    if (request.signal.aborted) return null

    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    if (activeRequest === request) {
      activeRequest = null
      store.setLoading(false)
    }
  }
}
